import { TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { ButtonGroup } from "src/components/button";
import SelectMenu from "src/components/selectMenu";
import CustomToggle from "src/components/toggles/customToggle";
import { useDomainsList } from "src/hooks/useDomainsList";
import { useEnvironments } from "src/hooks/useEnvironments";
import { SimpleModal } from "src/layouts/modal/simpleModal";
import { isFieldWithValue } from "src/utils";

interface AddEditDomainModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  submitTitleButton?: string;
  onSubmit: (data: any) => void;
  defaultValues?: any;
  isLoading?: boolean;
  organizationId: string;
  isUpdate?: boolean;
}

interface IFormErrors {
  [key: string]: string;
}

const defaultFormValues = {
  id: "",
  host: "",
  environmentID: "",
  clusterID: "",
  isUnsecure: true,
  certificateSecretNamespace: "",
  certificateSecretName: "",
  scopeIndex: 0,
};

export default function AddEditDomainForm({
  open,
  onClose,
  onSubmit,
  title = "Domain",
  description = "",
  submitTitleButton = "Add",
  defaultValues,
  isLoading = false,
  organizationId,
  isUpdate = false,
}: Readonly<AddEditDomainModalProps>) {
  const [formData, setFormData] = useState(defaultFormValues);
  const [formErrors, setFormErrors] = useState<IFormErrors>({});
  const {
    environments,
    isLoadingEnv,
    getEnvironments,
  } = useEnvironments({ organizationId });

  const {
    clusters,
    fetchClusters
  } = useDomainsList({ organizationId });

  useEffect(() => {
    fetchClusters();
    getEnvironments()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) {
      setFormData(defaultFormValues);
      setFormErrors({});
    }
  }, [open]);

  useEffect(() => {
    if(defaultValues){
      setFormData({
        id: defaultValues?.id || "",
        host: defaultValues?.host || "",
        environmentID: defaultValues?.environmentID || "",
        clusterID: defaultValues?.clusterID || "",
        isUnsecure:
          defaultValues?.isUnsecure !== null &&
          defaultValues?.isUnsecure !== undefined
            ? defaultValues?.isUnsecure
            : true,
        certificateSecretNamespace:
        defaultValues?.certificateSecretNamespace || "",
        certificateSecretName: defaultValues?.certificateSecretName || "",
        scopeIndex: getScopeIndex(defaultValues),
      });

    }
  }, [defaultValues]);

  const getScopeIndex = (values: any) => {
    if (values?.environmentID) return 1;
    return 0;
  };
  function isWildcardDomain(host: string) {
    return typeof host === "string" && host.startsWith("*.");
  }
  const isValidForm = () => {
    const newErrors: IFormErrors = {};
    const validations = [
      {
        condition: !isFieldWithValue(formData.host) || isWildcardDomain(formData.host) ,
        field: "host",
        message: "Host is required or not valid",
      },
      {
        condition:
          formData.scopeIndex === 0 && !isFieldWithValue(formData.clusterID),
        field: "clusterID",
        message: "Cluster is required",
      },
      {
        condition:
          formData.scopeIndex === 1 &&
          !isFieldWithValue(formData.environmentID),
        field: "environmentID",
        message: "Environment is required",
      },
      {
        condition:
          !formData.isUnsecure &&
          !isFieldWithValue(formData.certificateSecretNamespace),
        field: "certificateSecretNamespace",
        message: "Secret Namespace is required",
      },
      {
        condition:
          !formData.isUnsecure &&
          !isFieldWithValue(formData.certificateSecretName),
        field: "certificateSecretName",
        message: "Secret Name is required",
      },
    ];

    validations.forEach(({ condition, field, message }) => {
      if (condition) {
        newErrors[field] = message;
      }
    });
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    try {
      if (!isValidForm()) {
        return;
      }
      onSubmit(formData);
    } catch (error) {
      console.log({ error });
    }
  };

  const handleFormChange = (field: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }));
      setFormErrors((prev) => ({
        ...prev,
        [field]: ""
      }));
  };
  const handleCloseModal = () => {
    onClose()
    setFormErrors({});
    setFormData(defaultFormValues);
  }
  return (
    <SimpleModal
      open={open}
      onClose={handleCloseModal}
      title={title}
      onSubmit={handleSubmit}
      description={description}
      submitTitleButton={submitTitleButton}
      maxWidth="sm"
      isLoading={isLoading}
    >
      <div className="flex flex-col items-start justify-start">
        <div className="grid grid-cols-1 grid-rows-1 flex-row gap-4 w-full items-baseline">
          <div className="flex flex-col gap-2">
            <label
              className="text-sm font-medium-500 text-primary"
              htmlFor="host"
            >
              Host <b className="text-red-500">*</b>
            </label>
            <TextField
              fullWidth
              placeholder="e.g., api.example.com"
              variant="outlined"
              size="small"
              name="host"
              value={formData.host}
              onChange={(e) => handleFormChange("host", e.target.value)}
              error={!!formErrors.host}
              helperText={formErrors.host}
              disabled={isLoading || isUpdate}
            />
          </div>
         <div className="flex flex-col gap-2">
            <label
              className="text-sm font-medium-500 text-primary"
              htmlFor="scope"
            >
              Scope <b className="text-red-500">*</b>
            </label>
            <ButtonGroup
              buttons={["Cluster", "Environment"]}
              onClick={(index) => handleFormChange("scopeIndex", index)}
              activeIndex={formData.scopeIndex}
              disabled={isUpdate}
            />
          </div>
         {formData.scopeIndex === 0 && (
            <div className="grid grid-cols-1 grid-rows-1 flex-row gap-4 w-full items-baseline mt-2">
              <div className="flex flex-col gap-2">
                <label
                  className="text-sm font-medium-500 text-primary"
                  htmlFor="clusterID"
                >
                  Cluster <b className="text-red-500">*</b>
                </label>
                <SelectMenu
                  options={clusters}
                  onChange={(value) => handleFormChange("clusterID", value)}
                  placeholder="Select a cluster"
                  defaultValue={formData.clusterID}
                  disabled={isLoading || isUpdate}
                  error={!!formErrors.clusterID}
                  helpertext={formErrors.clusterID}
                  height="9rem"
                  className={
                    !!formErrors.clusterID
                      ? "border border-red-500 rounded-md"
                      : ""
                  }
                />
              
              </div>
            </div>
          )}
          {formData.scopeIndex === 1 && (
            <div className="grid flex-row gap-4 w-full items-baseline mt-2">
              <div className="flex flex-col gap-2">
                <label
                  className="text-sm font-medium-500 text-primary"
                  htmlFor="environmentID"
                >
                  Environment Field
                </label>
                <SelectMenu
                  options={environments}
                  onChange={(value) => handleFormChange("environmentID", value)}
                  getRowData={(row) => handleFormChange("clusterID", row.other.clusterID)}
                  placeholder="Select scope"
                  defaultValue={formData.environmentID}
                  disabled={
                    isLoadingEnv || isUpdate
                  }
                  height="9rem"
                  error={!!formErrors.environmentID}
                  helpertext={formErrors.environmentID}
                  className={`${
                    !!formErrors.environmentID
                      ? "border border-red-500 rounded-md"
                      : ""
                  }`}
                />
              </div>
            </div>
          )}
           <div className="grid grid-cols-1 grid-rows-1 flex-row gap-4 w-full items-baseline mt-2">
            <div className="flex flex-col gap-2">
              <label
                className="text-sm font-medium-500 text-primary"
                htmlFor="isUnsecure"
              >
                Security
              </label>
              <CustomToggle
                isToggled={!formData.isUnsecure}
                onToggle={(value) => handleFormChange("isUnsecure", !value)}
                label="Enable Security"
                disabled={isLoading}
              />
            </div>
          </div>
          {!formData.isUnsecure && (
            <div className="grid grid-cols-2 grid-rows-1 flex-row gap-4 w-full items-baseline mt-2">
              <div className="flex flex-col gap-2">
                <label
                  className="text-sm font-medium-500 text-primary"
                  htmlFor="certificateSecretNamespace"
                >
                  Secret Namespace <b className="text-red-500">*</b>
                </label>
                <TextField
                  fullWidth
                  placeholder="e.g., default-namespace"
                  variant="outlined"
                  size="small"
                  name="certificateSecretNamespace"
                  value={formData.certificateSecretNamespace}
                  onChange={(e) =>
                    handleFormChange(
                      "certificateSecretNamespace",
                      e.target.value
                    )
                  }
                  error={!!formErrors.certificateSecretNamespace}
                  helperText={formErrors.certificateSecretNamespace}
                  disabled={isLoading}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  className="text-sm font-medium-500 text-primary"
                  htmlFor="certificateSecretName"
                >
                  Secret name <b className="text-red-500">*</b>
                </label>
                <TextField
                  fullWidth
                  placeholder="e.g., Region-1"
                  variant="outlined"
                  size="small"
                  name=""
                  value={formData.certificateSecretName}
                  onChange={(e) =>
                    handleFormChange("certificateSecretName", e.target.value)
                  }
                  error={!!formErrors.certificateSecretName}
                  helperText={formErrors.certificateSecretName}
                  disabled={isLoading}
                />
              </div>
            </div>
          )}
        </div>
      </div> 
    </SimpleModal>
  );
}
